<script setup lang="ts">
useHead({
  title: "Zenapptic AI - Experiential Spaces",
});
</script>

<template>
  <SolutionsLayout>
    <SolutionsDetailsTitle
      :background-url="`${useCdnUrl()}/assets/solutions/experiential-spaces/experiential-spaces-placeholder.png`"
    >
      <template #videos>
        <source
          :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/experiential-spaces-background.webm`"
          type="video/webm"
        />
      </template>

      <template #title><b>EXPERIENTIAL</b><br />SPACES</template>

      <template #description>
        Engage your audience in a whole new way with
        <b>interactive visual experiences</b> for any space.
      </template>
    </SolutionsDetailsTitle>

    <div class="feature-content">
      <SolutionsDetailsBlock>
        <template #image>
          <ImageMontage>
            <img
              :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/interactive-lobbies-1.webp`"
              alt="Interactive Lobbies"
            />
            <img
              :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/interactive-lobbies-2.webp`"
              alt="Interactive Lobbies"
            />
          </ImageMontage>
        </template>

        <template #title>Interactive Lobby</template>
        <template #description>
          <p>
            Transform your lobby, entrance, or any space into an interactive realm, where visitors
            can engage in an informative, personalized, and fun experience. We call these
            <b>Zen Spaces</b>:
          </p></template
        >
        <template #items>
          <ul>
            <li>
              <b>Virtual/Real Worlds:</b> Create a unique experience with 3D views of real or
              virtual worlds that transport visitors to another realm
            </li>
            <li>
              <b>Interactivity & Gamification:</b> Add interactivity using AR on smartphones, QR
              code-triggered experiences, or add fun games and personalized activities
            </li>
            <li>
              <b>Real-Time Content:</b> Add real-time information or data from any source to keep
              the experience relevant and current
            </li>
          </ul>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/experiential-spaces/lobbyverse.webp`"
      >
        <template #details-left>
          <h2>
            Check out our<br /><b>Lobbyverse<sup>™</sup></b>
          </h2>

          <ul>
            <li>
              <h3>Turn your lobby into a destination:</h3>
              Create unique experiences where digital display walls become windows into any real or
              virtual world
            </li>
          </ul>
        </template>

        <template #details-right>
          <VideoPlayer
            :video-url="`${useCdnUrl()}/assets/solutions/experiential-spaces/lobbyverse-video.webm`"
            ><img
              :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/lobbyverse-placeholder.png`"
              alt="Lobbyverse"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/immersive-retail.webp`"
            alt="Immersive retail"
            style="height: 350px"
          />
        </template>

        <template #title>Immersive Retail</template>
        <template #description>
          <div>
            <p>
              Revolutionize the in-store experience and captivate customers with our Immersive
              Retail Solutions
            </p>

            <ul>
              <li>Brand storytelling with interactive videowalls</li>

              <li>In-store entertainmentt</li>

              <li>Dynamic promotions</li>
            </ul>
          </div>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/experiential-spaces/rendering-engine.webp`"
        style="padding: 18rem 2rem"
      >
        <template #details-left>
          <h2>Experiences <b>powered</b> by the<br /><b>ZEN3</b> Rendering Engine</h2>
        </template>

        <template #details-right>
          <div>
            <TheCtaButton
              style="background: black"
              :href="`${useCdnUrl()}/assets/solutions/experiential-spaces/Zen3-rendering-engine-product-details.pdf`"
              target="_blank"
              >Download Product Sheet</TheCtaButton
            >
          </div>
        </template>
      </SolutionsCalloutBlock>

      <SolutionsDetailsBlock>
        <template #image>
          <img
            :src="`${useCdnUrl()}/assets/solutions/experiential-spaces/concrete-jungle.webp`"
            alt="Concrete Jungle"
          />
        </template>

        <template #title>Public Spaces</template>
        <template #description
          ><p>
            Combining technology and creativity, public immersive experiences have the potential to
            transform the way people interact with their surroundings, fostering a sense of wonder,
            learning, and community engagement
          </p>
        </template>
      </SolutionsDetailsBlock>

      <SolutionsCalloutBlock
        :background-url="`${useCdnUrl()}/assets/solutions/experiential-spaces/concrete-jungle-background.webp`"
      >
        <template #details-left>
          <h2><b>Concrete Jungle</b><br />in Times Square</h2>
          <p>
            One of the largest location-based AR experiences in North America delivered with
            <b>ZEN3</b>
          </p></template
        >

        <template #details-right>
          <VideoPlayer :video-url="`${useCdnUrl()}/assets/video/concrete-jungle-video.webm`"
            ><img
              :src="`${useCdnUrl()}/assets/video/concrete-jungle-poster.webp`"
              alt="Concrete Jungle"
          /></VideoPlayer>
        </template>
      </SolutionsCalloutBlock>
    </div>

    <ContactBlock />
  </SolutionsLayout>
</template>
